import {
    Button,
    Column,
    Modal,
    ProgressBar,
    Row,
    Typography,
    useAlert
} from "matsuri-ui"
import { css } from "@emotion/react"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useNavigate, useParams } from "react-router-dom"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled from "@emotion/styled"
import CloseIcon from "@mui/icons-material/Close"
import { useMobileLayoutProps } from "@/hooks/useMobileLayoutProps"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import {
    FileObject,
    GridPhotoUploader,
    PhotoUploaderThumbnail
} from "@/components/GridPhotoUploader/GridPhotoUploader"
import { Header } from "@/components/Header/Header"
import { ReportFooter } from "@/components/Footer/ReportFooter"
import { assertIsDefined } from "@/utils/assetIsDefined"
import { genCleaningReportPagePath } from "@/routes/protected"
import {
    requestDeleteCleaningEvidence,
    requestUploadCleaningEvidence
} from "@/hooks/useCleaningEvidences"
import { useCleaningInfoDetail } from "@/hooks/useCleaningInfos"
import { BackToDetailConfirmDialog } from "@/components/Dialog/BackToDetailConfirmDialog"
import { useNavigateBackToDetail } from "@/hooks/useNavigateBackToDetail"
import { useTranslation } from "@/hooks/useTranslation"

const UploadArea = styled.div`
    padding: 16px 16px 108px 16px; // 108pxはFooterの高さ + 余白16px
`

const ThumbnailArea = styled.div`
    img {
        max-width: 100%;
    }
`

const CenterTextView = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    font-weight: bold;
    padding: 1em;
`

const BlobThumbnail = ({ blob }: { blob?: Blob }) => {
    const objectURL = useMemo(
        () => (blob ? URL.createObjectURL(blob) : undefined),
        [blob]
    )

    useEffect(() => {
        return () => {
            if (objectURL) {
                URL.revokeObjectURL(objectURL)
            }
        }
    }, [objectURL])

    return <img src={objectURL} />
}

export const EvidencePage = () => {
    const { t } = useTranslation()
    const { cleaningId } = useParams<"cleaningId">()

    assertIsDefined(cleaningId)

    const { token } = useAuth()
    const { data: cleaningDetail } = useCleaningInfoDetail(token, cleaningId)

    // 清掃詳細画面に戻る確認モーダル
    const {
        confirmBackOpen,
        openConfirmBack,
        closeConfirmBack,
        goBackToDetail
    } = useNavigateBackToDetail("cleaning", cleaningId)

    const remainingErrands = useMemo(() => {
        return (
            cleaningDetail?.errands?.filter(
                errand => errand.pictures.length === 0
            ) || []
        )
    }, [cleaningDetail])

    const navigate = useNavigate()

    const handleFinishEvidence = useCallback(() => {
        if (remainingErrands?.length != 0) {
            navigate(genCleaningReportPagePath(cleaningId, "ready-for-errands"))
        } else {
            navigate(genCleaningReportPagePath(cleaningId, "checkitems"))
        }
    }, [cleaningId, navigate, remainingErrands?.length])

    const { rootProps, bodyProps, footerProps } = useMobileLayoutProps()

    // 既存の写真の削除を管理しないといけない+既存の写真と新規の写真は管理が別なので雑にrefetchできないためここでステートとして管理
    // 削除済みURL集合を別途持つことで、既存evidencesから派生させる
    const evidences = useMemo(
        () => cleaningDetail?.evidences,
        [cleaningDetail?.evidences]
    )
    const [deletedUrls, setDeletedUrls] = useState<Set<string>>(new Set())
    // cleaningId 切り替え時に削除済みURLをリセット（同一コンポーネント再利用時に前回の削除状態が残るのを防ぐ）
    const [prevCleaningId, setPrevCleaningId] = useState<string | undefined>(
        undefined
    )
    if (cleaningId !== prevCleaningId) {
        setPrevCleaningId(cleaningId)
        setDeletedUrls(new Set())
    }
    const existingEvidences = useMemo(
        () => evidences?.filter(e => !deletedUrls.has(e.url)),
        [evidences, deletedUrls]
    )

    // pictureKeyからimageIdを引くためのMap(削除のために使う、viewには影響を与えないのでRefとして宣言)
    const imageIdMap = useRef<Record<string, string | undefined>>({})
    useEffect(() => {
        if (evidences) {
            for (const evidence of evidences) {
                // 既存ファルはurlをpictureKeyとして使っている(ポップアップダイアログで画像を表示する必要があるため; 割とevil)
                imageIdMap.current[evidence.url] = evidence.imageId
            }
        }
    }, [evidences])

    // 写真をあげて全て削除した時もunusedメッセージを表示してあげるべきな気がするが面倒なのでサボる
    const [used, setUsed] = useState(false)

    const [completed, setCompleted] = useState(true)
    const handleComplete = useCallback(() => {
        setCompleted(true)
    }, [])

    const [files, setFiles] = useState<File[]>()
    const handleSelectFiles = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = event.currentTarget.files

            if (files) {
                setFiles(Array.from(files))

                // ファイル選択ダイアログを開いた後キャンセルするケースがあるのでこれらをifの外に書かない
                setUsed(true)
                setCompleted(false)
            }
        },
        []
    )
    const addPictureButtonRef = useRef<HTMLInputElement>(null)
    const handleAddPicture = useCallback(() => {
        addPictureButtonRef.current?.click()
    }, [])

    const [open, setOpen] = useState(false)
    const handleClose = useCallback(() => {
        setOpen(false)
    }, [])
    const [currentPicture, setCurrentPicture] = useState<FileObject | string>()
    const [currentDeleteCallback, setCurrentDeleteCallback] =
        useState<() => void>()

    const { addAlert } = useAlert()

    const handleClickPicture = useCallback(
        (fileObject: FileObject, onDelete: () => void) => {
            setCurrentPicture(fileObject)
            setCurrentDeleteCallback(_prev => async () => {
                const imageId = imageIdMap.current[fileObject.key]
                if (imageId) {
                    const { err } = await requestDeleteCleaningEvidence(
                        token,
                        cleaningId,
                        imageId
                    )
                    if (err) {
                        addAlert(t("写真の削除に失敗しました"), {
                            severity: "error",
                            duration: 2500
                        })
                    } else {
                        onDelete()
                        imageIdMap.current[fileObject.key] = undefined
                    }
                }

                setOpen(false)
            })
            setOpen(true)
        },
        [addAlert, cleaningId, token]
    )

    const handleClickUploadedPicture = useCallback(
        (url: string) => {
            setCurrentPicture(url)
            setCurrentDeleteCallback(_prev => async () => {
                const imageId = imageIdMap.current[url]
                if (imageId) {
                    const { err } = await requestDeleteCleaningEvidence(
                        token,
                        cleaningId,
                        imageId
                    )
                    if (err) {
                        addAlert(t("写真の削除に失敗しました"), {
                            severity: "error",
                            duration: 2500
                        })
                    } else {
                        setDeletedUrls(prev => {
                            const next = new Set(prev)
                            next.add(url)
                            return next
                        })
                        imageIdMap.current[url] = undefined
                    }
                }

                setOpen(false)
            })
            setOpen(true)
        },
        [addAlert, cleaningId, token]
    )

    const handleUploadPicture = useCallback(
        async (pictureKey: string, file: Blob) => {
            const { data, err } = await requestUploadCleaningEvidence(
                token,
                cleaningId,
                file
            )

            if (err) {
                addAlert(t("写真のアップロードに失敗しました"), {
                    severity: "error",
                    duration: 2500
                })
            } else {
                imageIdMap.current[pictureKey] = data.imageId
            }
        },
        [addAlert, cleaningId, token]
    )

    const [progress, setProgress] = useState({
        total: 0,
        uploaded: 0
    })

    return (
        <>
            <Modal
                backdrop
                open={open}
                onClose={handleClose}
                header={<Typography variant="h3">{t("写真を確認")}</Typography>}
                body={
                    <Column gap={1}>
                        <div>
                            <Typography>
                                {t("削除ボタンを押して削除できます")}
                            </Typography>
                        </div>
                        <div>
                            <ThumbnailArea>
                                {typeof currentPicture === "string" ? (
                                    <img src={currentPicture} />
                                ) : (
                                    <BlobThumbnail
                                        blob={currentPicture?.compressedData}
                                    />
                                )}
                            </ThumbnailArea>
                        </div>
                    </Column>
                }
                footer={
                    <Row gap={2} justify="flex-end">
                        <div>
                            <Button onClick={handleClose}>
                                {t("キャンセル")}
                            </Button>
                        </div>
                        <div>
                            <Button
                                variant="filled"
                                color="error"
                                onClick={currentDeleteCallback}
                            >
                                {t("削除")}
                            </Button>
                        </div>
                    </Row>
                }
            />

            <main {...rootProps}>
                <Header
                    css={css`
                        padding-top: env(safe-area-inset-top, 0px);
                    `}
                >
                    <MobileAppHeader
                        title={
                            <Typography variant="h4">
                                {t("清掃写真をアップロード")}
                            </Typography>
                        }
                        onNext={openConfirmBack}
                        nextIcon={<CloseIcon />}
                    />
                </Header>

                {/* 清掃詳細画面に戻る確認モーダル */}
                <BackToDetailConfirmDialog
                    type="cleaning"
                    open={confirmBackOpen}
                    onClose={closeConfirmBack}
                    onConfirm={goBackToDetail}
                />

                <div {...bodyProps}>
                    {used ? (
                        <UploadArea>
                            <GridPhotoUploader
                                selectedFiles={files}
                                requestUploadPicture={handleUploadPicture}
                                onClick={handleClickPicture}
                                onCompleted={handleComplete}
                                onChangeProgress={setProgress}
                            />
                        </UploadArea>
                    ) : (
                        <CenterTextView>
                            {t(
                                "「写真を追加」から写真を選択してください。選択したものが順次アップロードされます。"
                            )}
                        </CenterTextView>
                    )}

                    <UploadArea>
                        <div
                            css={css`
                                display: grid;
                                grid-template-columns: repeat(3, 1fr);
                                gap: 16px;
                            `}
                        >
                            {existingEvidences?.map(e => (
                                // アップロード済みの写真もcomponent内で扱えるようにするとコンポーネント(の状態管理)が複雑になりすぎるのでexportされてるものやstateを悪用してアプリ側でどうにかする
                                <PhotoUploaderThumbnail
                                    key={e.url}
                                    compressing={false}
                                    requested={true}
                                    uploaded={true}
                                    src={e.url}
                                    pictureKey={e.url}
                                    onClickPicture={handleClickUploadedPicture}
                                />
                            ))}
                        </div>
                    </UploadArea>
                </div>

                <ReportFooter {...footerProps}>
                    <Column gap={1}>
                        {progress.total > 0 && (
                            <div
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                    text-align: center;
                                `}
                            >
                                <ProgressBar
                                    color="primary"
                                    value={progress.uploaded}
                                    max={progress.total}
                                />
                                <Typography>
                                    {progress.uploaded}/{progress.total}
                                </Typography>
                            </div>
                        )}
                        <div
                            css={css`
                                display: grid;
                                grid-template-columns: 1fr 1fr;
                                gap: 16px;
                            `}
                        >
                            <div>
                                <input
                                    hidden
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={handleSelectFiles}
                                    ref={addPictureButtonRef}
                                />
                                <Button
                                    variant="filled"
                                    color="primary"
                                    fullWidth
                                    onClick={handleAddPicture}
                                >
                                    {t("写真を追加")}
                                </Button>
                            </div>
                            <div>
                                <Button
                                    variant="filled"
                                    color="primary"
                                    fullWidth
                                    onClick={handleFinishEvidence}
                                    disabled={!completed}
                                >
                                    {t("次へ進む")}
                                </Button>
                            </div>
                        </div>
                    </Column>
                </ReportFooter>
            </main>
        </>
    )
}
